// ============================================================================
// src/lib/processorXlsx.js — the broiler processor spreadsheet reader.
// ============================================================================
// Extracted verbatim from App() in src/main.jsx (B2.3). The parsing logic is
// unchanged; what changed is that it no longer reaches for React setters. It
// returns a discriminated result and the caller decides what the user sees:
//
//   {status: 'ok', parsed}          a recognized layout was read
//   {status: 'unrecognized'}        the file is fine, no layout matched
//   {status: 'loader-error'}        SheetJS could not be lazy-loaded
//   {status: 'error', message}      the workbook threw while being read
//
// SheetJS is loaded on first use only. It is ~900KB and most sessions never
// import a processor sheet, so it stays out of the main bundle and attaches
// to window.XLSX through window._wcfLoadXLSX (installed by initRuntime).
// ============================================================================

/* global XLSX */

// ── the runtime surface this module borrows ─────────────────────────────────
//
// SheetJS is attached to `window.XLSX` by window._wcfLoadXLSX (installed by
// initRuntime), so it is a global here rather than an import — the whole point
// is to keep the ~900KB library out of the main bundle. The declarations below
// describe ONLY the four entry points this parser touches. A cell value is
// `unknown`: a worksheet cell holds a string, number, boolean or Date depending
// on the column, and every read below already coerces with String()/parseFloat.
type XlsxApi = {
  read(data: ArrayBuffer, opts: {type: 'array'; cellDates: boolean}): XlsxWorkbook;
  utils: {
    decode_range(ref: string): {s: {r: number; c: number}; e: {r: number; c: number}};
    encode_cell(addr: {r: number; c: number}): string;
  };
};

// A worksheet mixes two kinds of key: '!'-prefixed metadata (only '!ref' is
// read here) and cell addresses holding `{v: value}` objects. One index
// signature cannot describe both, so cells are read through cellValue() below,
// which is where the `.v` access lives.
type XlsxWorksheet = {
  '!ref'?: string;
  [address: string]: unknown;
};
type XlsxWorkbook = {SheetNames: string[]; Sheets: {[name: string]: XlsxWorksheet | undefined}};

// The decoded sheet extent, as returned by XLSX.utils.decode_range. Named
// because findHeaderRow takes one as a parameter.
type SheetRange = {s: {r: number; c: number}; e: {r: number; c: number}};

const cellValue = (cell: unknown): unknown =>
  typeof cell === 'object' && cell !== null && 'v' in cell ? cell.v : undefined;

declare global {
  // SheetJS attaches itself to `window`, so it is reachable here as a bare
  // global rather than a module binding — which is also why it is declared in
  // global scope: a module-level `declare const` would read as an unused local.
  const XLSX: XlsxApi;

  interface Window {
    // Installed by initRuntime; resolves once SheetJS has attached itself to
    // window.XLSX. Optional because a page that never imports a processor sheet
    // never installs it.
    _wcfLoadXLSX?: () => Promise<unknown>;
  }
}

// One aggregation bucket keyed by lower-cased description.
type CutAggregate = {total: number; count: number; label: string};

// The parsed workbook summary. Every metric is optional because a given sheet
// may only carry some of them — `Object.keys(parsed).length > 1` (i.e. more
// than just fileName) is the module's own "did we recognize anything" test.
export type ParsedProcessorSheet = {
  fileName: string;
  avgDressed?: number;
  totalLbsWhole?: number;
  avgBreast?: number;
  avgThigh?: number;
  totalLbsCuts?: number;
};

export type ProcessorWorkbookResult =
  | {status: 'ok'; parsed: ParsedProcessorSheet}
  | {status: 'unrecognized'}
  | {status: 'loader-error'}
  | {status: 'error'; message: string};

// Wings are a sellable cut — only neck/feet/back are scrap-style categories
// that should be left out of the cuts total.
const EXCLUDED_FROM_CUTS = ['neck', 'feet', 'back', 'grand total'];

// Map an aggregated {label: {total, count, label}} dict into the parsed shape
// (avgDressed/avgBreast/avgThigh/totalLbsWhole/totalLbsCuts). Used by both the
// raw-format and pivot-format paths.
function aggToParsed(agg: Record<string, CutAggregate>, fileName: string): ParsedProcessorSheet {
  const out: ParsedProcessorSheet = {fileName};
  let cuts = 0;
  for (const key of Object.keys(agg)) {
    const a = agg[key];
    const avgWt = a.count > 0 ? a.total / a.count : 0;
    if (key.includes('whole chicken')) {
      if (avgWt > 0) out.avgDressed = Math.round(avgWt * 100) / 100;
      if (a.total > 0) out.totalLbsWhole = Math.round(a.total * 10) / 10;
    }
    if (key.includes('breast') && avgWt > 0) out.avgBreast = Math.round(avgWt * 100) / 100;
    if (key.includes('thigh') && avgWt > 0) out.avgThigh = Math.round(avgWt * 100) / 100;
    if (
      key !== 'grand total' &&
      !EXCLUDED_FROM_CUTS.some((e) => key.includes(e)) &&
      !key.includes('whole chicken') &&
      a.total > 0
    ) {
      cuts += a.total;
    }
  }
  if (cuts > 0) out.totalLbsCuts = Math.round(cuts * 10) / 10;
  return out;
}

// Locate a header row and the columns it names, within the first `maxRows`
// rows of a sheet. Every name in `required` must be present for a row to
// qualify; `optional` names come back as -1 when absent. First match wins if a
// header name is duplicated. Returns {row, cols} or null.
//
// The whole candidate row is read BEFORE any column is assigned. The original
// scans assigned during a single left-to-right pass, guarded by `hRow === r`,
// so a header sitting to the LEFT of its anchor was visited before the anchor
// had set the row and was silently missed. Both the raw and the pivot path had
// that defect.
// `fromRow` resumes the scan past a candidate that did not pan out; the window
// stays anchored at the sheet start so it is still "the first maxRows rows".
function findHeaderRow(
  cv: Record<string, unknown>,
  range: SheetRange,
  required: string[],
  optional: string[] = [],
  maxRows = 5,
  fromRow: number | null = null,
): {row: number; cols: Record<string, number>} | null {
  const start = fromRow == null ? range.s.r : fromRow;
  for (let r = start; r <= Math.min(range.s.r + maxRows, range.e.r); r++) {
    const cells: Array<[number, string]> = [];
    for (let c = range.s.c; c <= range.e.c; c++) {
      cells.push([
        c,
        String(cv[XLSX.utils.encode_cell({r, c})] || '')
          .trim()
          .toLowerCase(),
      ]);
    }
    const colOf = (name: string): number => {
      const hit = cells.find(([, v]) => v === name);
      return hit ? hit[0] : -1;
    };
    const cols: Record<string, number> = {};
    let complete = true;
    for (const name of required) {
      cols[name] = colOf(name);
      if (cols[name] < 0) complete = false;
    }
    if (!complete) continue;
    for (const name of optional) cols[name] = colOf(name);
    return {row: r, cols};
  }
  return null;
}

export async function parseProcessorWorkbook(file: File): Promise<ProcessorWorkbookResult> {
  try {
    // Lazy-load SheetJS on first use (saves ~900KB on every page load for users who don't import xlsx)
    if (typeof XLSX === 'undefined') {
      try {
        // A missing loader threw a TypeError here before and was reported as
        // 'loader-error'; the explicit throw keeps that exact routing rather
        // than optional-chaining past it into a later ReferenceError on XLSX.
        const loadXLSX = window._wcfLoadXLSX;
        if (!loadXLSX) throw new Error('window._wcfLoadXLSX is not installed');
        await loadXLSX();
      } catch {
        return {status: 'loader-error'};
      }
    }
    const buf = await file.arrayBuffer();
    const wb2 = XLSX.read(buf, {type: 'array', cellDates: false});
    let parsed: ParsedProcessorSheet | null = null;

    for (const sheetName of wb2.SheetNames) {
      const ws = wb2.Sheets[sheetName];
      if (!ws || !ws['!ref']) continue;

      // Build a cell value map: address -> value
      const cv: Record<string, unknown> = {};
      Object.keys(ws).forEach((addr) => {
        if (addr[0] === '!') return;
        cv[addr] = cellValue(ws[addr]);
      });
      const range = XLSX.utils.decode_range(ws['!ref']);

      // ── PATH 1: Raw per-package format (Sonny's standard layout).
      // Header within first 5 rows containing "Description" + "Weight"
      // columns; one data row per package.
      //
      // A qualifying row that yields NO data rows is not accepted as the header
      // — the scan resumes past it. "Description" and "Weight" are ordinary
      // words, so a legend or banner row can contain both; the previous
      // left-to-right scan happened to skip such a row only when its cells were
      // in the "wrong" order, which was accident rather than intent. Requiring
      // the header to actually produce packages is the real test, and it makes
      // a decoy row harmless whichever order its cells are in.
      let rawFrom: number | null = null;
      let rawHdr: {row: number; cols: Record<string, number>} | null;
      while ((rawHdr = findHeaderRow(cv, range, ['description', 'weight'], [], 5, rawFrom))) {
        const descCol = rawHdr.cols.description;
        const weightCol = rawHdr.cols.weight;
        const agg: Record<string, CutAggregate> = {};
        for (let r = rawHdr.row + 1; r <= range.e.r; r++) {
          const desc = String(cv[XLSX.utils.encode_cell({r, c: descCol})] || '').trim();
          // parseFloat performs this ToString on its argument already; naming
          // it makes the cell's unknown type visible instead of implicit.
          const wt = parseFloat(String(cv[XLSX.utils.encode_cell({r, c: weightCol})]));
          if (!desc || !isFinite(wt) || wt <= 0) continue;
          const key = desc.toLowerCase();
          let bucket = agg[key];
          if (!bucket) {
            bucket = {total: 0, count: 0, label: desc};
            agg[key] = bucket;
          }
          bucket.total += wt;
          bucket.count += 1;
        }
        if (Object.keys(agg).length > 0) {
          parsed = aggToParsed(agg, file.name);
          break;
        }
        rawFrom = rawHdr.row + 1;
      }
      if (parsed && Object.keys(parsed).length > 1) break;

      // ── PATH 2: Pivot summary fallback ("Row Labels"/"Total Weight"/etc).
      //
      // Each value is read from its DETECTED column WHEN that column was found,
      // and from the historical position only when it was not.
      //
      // The previous code detected labelCol/totalWtCol/countCol/avgWtCol and
      // then ignored three of them, reading total and count positionally from
      // labelCol + 1 and labelCol + 2. A pivot with its columns in any other
      // order therefore imported whatever happened to sit beside the labels and
      // reported SUCCESS — silently wrong processing weights.
      //
      // Detection is not made mandatory, because that would REGRESS files that
      // imported correctly before. When the column order is canonical the
      // positional reads were right, so a sheet captioned "Count" rather than
      // "Count of Packages", or Excel's default "Sum of Weight" rather than
      // "Total Weight", parsed fine — and requiring the exact captions would
      // turn each of those into an unexplained 'unrecognized'. Per-column
      // fallback fixes the reordered sheet without giving up any sheet that
      // already worked, and it still uses real detection wherever a caption IS
      // recognised, so a reordered sheet with only one recognised caption is
      // read correctly too.
      //
      // The gate accepts "Total Weight" as an alternative anchor to "Average
      // Weight", which is a widening: the old gate gave up on a pivot that
      // named its total but not its average, even though that is MORE
      // information, not less. The sheet's own average column is never read —
      // aggToParsed derives the average from total / count, and that derived
      // value is authoritative.
      const pivotHdr = findHeaderRow(
        cv,
        range,
        ['row labels'],
        ['total weight', 'count of packages', 'average weight'],
      );
      if (!pivotHdr) continue;
      const labelCol = pivotHdr.cols['row labels'];
      const detectedTotal = pivotHdr.cols['total weight'];
      const detectedCount = pivotHdr.cols['count of packages'];
      const detectedAvg = pivotHdr.cols['average weight'];
      // Recognised as a pivot at all only when it names its total or its
      // average — the same evidence the original gate demanded, plus the
      // stronger of the two.
      if (detectedTotal < 0 && detectedAvg < 0) continue;

      const totalWtCol = detectedTotal >= 0 ? detectedTotal : labelCol + 1;
      const countCol = detectedCount >= 0 ? detectedCount : labelCol + 2;

      // A positional fallback is only honest when the sheet is silent about
      // that column. If it lands on a column the sheet NAMED as something else,
      // the layout is not the canonical one and the fallback would misread it —
      // e.g. [Row Labels, Count of Packages, Average Weight] has no total at
      // all, so labelCol + 1 is the COUNT, and [Row Labels, Total Weight,
      // Average Weight] has no count, so labelCol + 2 is the AVERAGE. Both
      // imported wrong numbers under the old positional reads and reported
      // success. Reject instead: 'unrecognized' is a warning and the file still
      // uploads as a normal attachment.
      const named = new Set<number>();
      if (detectedTotal >= 0) named.add(detectedTotal);
      if (detectedCount >= 0) named.add(detectedCount);
      if (detectedAvg >= 0) named.add(detectedAvg);
      if (detectedTotal < 0 && named.has(totalWtCol)) continue;
      if (detectedCount < 0 && named.has(countCol)) continue;
      const agg2: Record<string, CutAggregate> = {};
      for (let r = pivotHdr.row + 1; r <= range.e.r; r++) {
        const labelVal = cv[XLSX.utils.encode_cell({r, c: labelCol})];
        if (!labelVal) continue;
        const label = String(labelVal).trim();
        if (!label) continue;
        // String() is the branch's coercion, kept because `cv` is typed
        // Record<string, unknown>; parseFloat/parseInt perform this same
        // ToString on their argument already, so it is type-only, not
        // behavioural. The COLUMNS are main's detected ones — reading
        // labelCol + 1/+ 2 positionally is the silently-wrong import PR #113
        // fixed, and it must not come back through a merge.
        const totalWt = parseFloat(String(cv[XLSX.utils.encode_cell({r, c: totalWtCol})]));
        // Per-ROW fallback, unchanged from the original: a single malformed
        // count cell still counts as one package. Note this is a real limit —
        // a BLANK count on a whole-chicken row reports that row's whole total
        // as one bird's dressed weight. That behaviour predates this change and
        // is left alone here rather than altered silently alongside a fix.
        const count = parseInt(String(cv[XLSX.utils.encode_cell({r, c: countCol})]));
        if (!isFinite(totalWt) || totalWt <= 0) continue;
        agg2[label.toLowerCase()] = {total: totalWt, count: isFinite(count) && count > 0 ? count : 1, label};
      }
      if (Object.keys(agg2).length > 0) {
        parsed = aggToParsed(agg2, file.name);
        if (Object.keys(parsed).length > 1) break;
      }
    }

    if (parsed && Object.keys(parsed).length > 1) return {status: 'ok', parsed};
    // The parser couldn't find a recognized layout, but the upload still
    // proceeds and the file lands as a normal attachment — warning, not error.
    return {status: 'unrecognized'};
  } catch (e) {
    console.warn('Processor parse error:', e);
    // Same `e.message || 'Unknown error'` fallback, read through a typed catch.
    const message = typeof e === 'object' && e !== null && 'message' in e ? e.message : undefined;
    return {status: 'error', message: message ? String(message) : 'Unknown error'};
  }
}
